import React, { FC } from 'react';
import { Box, useBreakpointValue, useTheme } from '@chakra-ui/react';
import { motion } from 'motion/react';
import Button from 'ts/common/components/gallery/Button';
import CoverPhoto from 'ts/common/components/gallery/CoverPhoto';
import GalleryHeading from 'ts/common/components/gallery/GalleryHeading';
import StudioHeading from 'ts/common/components/gallery/StudioHeading';
import StudioLogo from 'ts/common/components/gallery/StudioLogo';
import { ILayoutProps } from './BaseLayout';
import { boxLeftAnimations, getAnimationProps } from './animations';

const BoxLeftLayout: FC<ILayoutProps> = ({
    galleryTitle,
    brandName,
    coverPhoto,
    coverPhotoUrl,
    eventBrandLogo,
    logoAltText,
    buttonText,
    buttonTextLarge,
    colorScheme,
    onClick,
    enableAnimations = true
}) => {
    const theme = useTheme();
    const galleryHeadingSize =
        useBreakpointValue({ base: 'md' as const, lg: 'md' as const }) ?? 'md';
    const studioLogoSize = useBreakpointValue({ base: 'md' as const, lg: 'lg' as const });

    return (
        <Box position="absolute" width="full" height="full" onClick={onClick}>
            {/* Cover image - full background */}
            <motion.div
                style={{
                    position: 'absolute',
                    width: '100%',
                    height: '100%',
                    inset: 0
                }}
                {...getAnimationProps(boxLeftAnimations.coverImage, enableAnimations)}
            >
                <CoverPhoto coverPhoto={coverPhoto} coverPhotoUrl={coverPhotoUrl} />
            </motion.div>

            {/* Desktop: White box positioned on the left side */}
            <motion.div
                style={{
                    position: 'absolute',
                    width: useBreakpointValue({ base: '240px', lg: '35%' }) || '35%',
                    height: useBreakpointValue({ base: '100%', lg: '80%' }) || '80%',
                    left: useBreakpointValue({ base: '0', lg: '0' }) || '0',
                    top: useBreakpointValue({ base: '0', lg: '10%' }) || '10%',
                    background:
                        colorScheme === 'dark' ? theme.colors.gray[700] : theme.colors.white,
                    display: useBreakpointValue({ base: 'none', lg: 'flex' }) || 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '40px 30px'
                }}
                {...(boxLeftAnimations.background &&
                    getAnimationProps(boxLeftAnimations.background, enableAnimations))}
            >
                {/* Studio Name/Logo - Top */}
                <motion.div
                    style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        width: '100%'
                    }}
                    {...getAnimationProps(boxLeftAnimations.studioName, enableAnimations)}
                >
                    {eventBrandLogo ? (
                        <StudioLogo
                            logo={eventBrandLogo}
                            logoAltText={logoAltText}
                            logoSize={studioLogoSize}
                        />
                    ) : (
                        <StudioHeading text={brandName} variant="primary" />
                    )}
                </motion.div>

                {/* Gallery Name - Center */}
                <motion.div
                    style={{
                        textAlign: 'center',
                        width: '100%'
                    }}
                    {...getAnimationProps(boxLeftAnimations.galleryName, enableAnimations)}
                >
                    <GalleryHeading
                        size={galleryHeadingSize}
                        text={galleryTitle}
                        variant="primary"
                    />
                </motion.div>

                {/* Button - Bottom */}
                <motion.div {...getAnimationProps(boxLeftAnimations.viewBtn, enableAnimations)}>
                    <Button
                        text={buttonText}
                        textLarge={buttonTextLarge}
                        variant="outline primary"
                    />
                </motion.div>
            </motion.div>

            {/* Mobile: Left side white panel with content */}
            <motion.div
                style={{
                    position: 'absolute',
                    width: useBreakpointValue({ base: '50%', lg: '35%' }) || '50%',
                    height: useBreakpointValue({ base: '80%', lg: '80%' }) || '80%',
                    left: useBreakpointValue({ base: '0', lg: '0' }) || '0',
                    top: useBreakpointValue({ base: '10%', lg: '10%' }) || '10%',
                    background:
                        colorScheme === 'dark' ? theme.colors.gray[700] : theme.colors.white,
                    display: useBreakpointValue({ base: 'flex', lg: 'none' }) || 'none',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '40px 20px'
                }}
                {...(boxLeftAnimations.background &&
                    getAnimationProps(boxLeftAnimations.background, enableAnimations))}
            >
                {/* Studio Name */}
                <motion.div
                    style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        width: 'fit-content'
                    }}
                    {...getAnimationProps(boxLeftAnimations.studioName, enableAnimations)}
                >
                    {eventBrandLogo ? (
                        <StudioLogo
                            logo={eventBrandLogo}
                            logoAltText={logoAltText}
                            logoSize={studioLogoSize}
                        />
                    ) : (
                        <StudioHeading text={brandName} variant="primary" />
                    )}
                </motion.div>

                {/* Gallery Name */}
                <motion.div
                    style={{
                        textAlign: 'center',
                        width: 'fit-content'
                    }}
                    {...getAnimationProps(boxLeftAnimations.galleryName, enableAnimations)}
                >
                    <GalleryHeading
                        size={galleryHeadingSize}
                        text={galleryTitle}
                        variant="primary"
                    />
                </motion.div>

                {/* Button */}
                <motion.div {...getAnimationProps(boxLeftAnimations.viewBtn, enableAnimations)}>
                    <Button
                        text={buttonText}
                        textLarge={buttonTextLarge}
                        variant="outline primary"
                    />
                </motion.div>
            </motion.div>
        </Box>
    );
};

export default BoxLeftLayout;
